import React, { useEffect, useRef } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { clsx } from 'clsx';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface ToastItem {
  id: string;
  message: string;
  variant: ToastVariant;
  duration?: number;
}

interface ToastProps {
  toast: ToastItem;
  onDismiss: (id: string) => void;
}

const VARIANT_CONFIG: Record<ToastVariant, {
  icon: React.ElementType;
  iconClass: string;
  barClass: string;
  borderClass: string;
}> = {
  success: {
    icon: CheckCircle2,
    iconClass: 'text-emerald-400',
    barClass: 'bg-emerald-500',
    borderClass: 'border-emerald-500/20',
  },
  error: {
    icon: XCircle,
    iconClass: 'text-red-400',
    barClass: 'bg-red-500',
    borderClass: 'border-red-500/20',
  },
  warning: {
    icon: AlertTriangle,
    iconClass: 'text-amber-400',
    barClass: 'bg-amber-500',
    borderClass: 'border-amber-500/20',
  },
  info: {
    icon: Info,
    iconClass: 'text-blue-400',
    barClass: 'bg-blue-500',
    borderClass: 'border-blue-500/20',
  },
};

const Toast: React.FC<ToastProps> = ({ toast, onDismiss }) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const duration = toast.duration ?? 4000;
  const config = VARIANT_CONFIG[toast.variant];
  const Icon = config.icon;

  useEffect(() => {
    timerRef.current = setTimeout(() => onDismiss(toast.id), duration);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [toast.id, duration, onDismiss]);

  return (
    <div
      className={clsx(
        'relative flex items-start gap-3 min-w-[280px] max-w-sm w-full',
        'bg-[#1a1a1a] border rounded-2xl shadow-2xl shadow-black/60 p-4',
        'animate-in slide-in-from-right-4 fade-in duration-300',
        config.borderClass,
      )}
      role="alert"
    >
      {/* Progress bar */}
      <div
        className={clsx('absolute bottom-0 left-0 h-0.5 rounded-b-2xl', config.barClass)}
        style={{
          animation: `toast-progress ${duration}ms linear forwards`,
        }}
      />

      <div className={clsx('mt-0.5 shrink-0', config.iconClass)}>
        <Icon size={18} />
      </div>

      <p className="flex-1 text-sm text-gray-200 leading-relaxed font-medium">
        {toast.message}
      </p>

      <button
        onClick={() => onDismiss(toast.id)}
        className="shrink-0 p-1 rounded-lg text-gray-500 hover:text-white hover:bg-white/10 transition-colors"
        aria-label="Dismiss notification"
      >
        <X size={14} />
      </button>
    </div>
  );
};

interface ToastContainerProps {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-2.5 items-end"
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
};
